import React, { useState, useEffect, useCallback } from 'react';
import { colors, radius } from '../../styles/theme';
import ErrorBanner from '../../components/ErrorBanner';
import { searchMatch } from '../../utils/searchMatch';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * İç Fiyatı Eksik — Faz 3 worklist.
 * TORUNA → marka iç satış kaydı olmayan (marka, malzeme) çiftleri. Bu malzemeler için
 * maliyet dış fiyat × marka marjı ile TAHMİN ediliyor (IC_SATIS_FALLBACK).
 * Backend: GET /api/recipes/ic-satis-fiyat-eksik (VIEW_RECIPE_COSTS).
 * Şirketler Arası Satış'tan satış girilince gerçek iç fiyata döner.
 */
const IcSatisFiyatEksikPage = () => {
  const [data, setData] = useState(null);
  const [markalar, setMarkalar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [arama, setArama] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/recipes/ic-satis-fiyat-eksik`, { headers: getAuthHeaders() });
      if (res.status === 401) { window.location.href = '/login'; return; }
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const err = await res.json(); if (err && err.detail) detail = err.detail; } catch (_) { /* JSON değil */ }
        throw new Error(detail);
      }
      setData(await res.json());
    } catch (err) {
      setError(err.message || 'Bilinmeyen hata');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // İşletme (marka) listesi — kod->ad eşlemesi için (DB'den, hardcode yasak — Anayasa M.1)
    fetch(`${API_URL}/api/markalar`, { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : []))
      .then(d => setMarkalar(Array.isArray(d) ? d : (d.markalar || [])))
      .catch(() => setMarkalar([]));
  }, [fetchData]);

  // marka kod/ad -> okunabilir ad. Backend 'marka' alanı kod da ad da olabilir; her ikisini de eşle.
  const markaAdiCoz = (marka) => {
    if (!marka) return '—';
    const m = markalar.find(x => x.kod === marka || x.ad === marka);
    return (m && m.ad) || marka;
  };

  if (loading && !data) return <div style={{ padding: 24 }}>Yükleniyor…</div>;

  const eksik = data?.eksik || [];
  const toplam = data?.toplam ?? eksik.length;

  const satirlar = eksik.filter(
    e => searchMatch(`${e.malzeme} ${markaAdiCoz(e.marka)} ${e.marka || ''}`, arama)
  );

  return (
    <div>
      {error && <div style={{ padding: '0 20px' }}><ErrorBanner error={error} onRetry={fetchData} /></div>}

      {/* Bilgi notu */}
      <div style={{ padding: '12px 20px 0', fontSize: '12px', color: colors.textSecondary, lineHeight: 1.5 }}>
        ℹ️ Bu malzemeler için TORUNA→marka iç satış kaydı yok; maliyet dış fiyat × marka marjı ile
        tahmin ediliyor. Şirketler Arası Satış'tan satış girilince gerçek fiyata döner.
      </div>

      {/* Özet rozeti + arama */}
      <div style={{ display: 'flex', gap: '8px', padding: '12px 20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{
          display: 'inline-block', padding: '5px 12px', borderRadius: radius.lg, fontSize: '13px',
          fontWeight: 'bold', background: toplam > 0 ? '#fff3e0' : '#e8f5e9',
          color: toplam > 0 ? '#bf360c' : '#2e7d32',
        }}>
          {toplam > 0 ? `${toplam} malzeme iç fiyatı bekliyor` : 'İç satış fiyatı eksik malzeme yok 🎉'}
        </span>
        <input
          placeholder="Ara (malzeme, işletme...)"
          value={arama}
          onChange={e => setArama(e.target.value)}
          style={{ flex: 1, minWidth: '200px', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}
        />
        <span style={{ alignSelf: 'center', fontSize: 12, color: colors.textTertiary }}>{satirlar.length} kayıt</span>
      </div>

      {/* Tablo */}
      <div style={{ padding: '0 20px', overflowX: 'auto' }}>
        {satirlar.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
            {toplam === 0 ? 'İç satış fiyatı eksik malzeme yok 🎉' : 'Aramaya uygun kayıt yok.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '7px', textAlign: 'left' }}>İşletme</th>
                <th style={{ padding: '7px', textAlign: 'left' }}>Malzeme</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Kullanan Reçete</th>
              </tr>
            </thead>
            <tbody>
              {satirlar.map((e, idx) => (
                <tr key={`${e.marka}-${e.malzeme_id}-${idx}`} style={{ borderBottom: `1px solid ${colors.border}` }}
                    onMouseEnter={ev => ev.currentTarget.style.background = '#f8f9fa'}
                    onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '10px', fontWeight: 'bold' }}>{markaAdiCoz(e.marka)}</td>
                  <td style={{ padding: '7px' }}>{e.malzeme}</td>
                  <td style={{ padding: '7px', textAlign: 'right' }}>{e.recete_sayisi ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default IcSatisFiyatEksikPage;
